/**
 * O estado do texto do documento da mesa (E202.F3, ADR-0145 §10; E205.F2, ADR-0147 §2).
 *
 * A conferência final e a assinatura consomem a última versão salva: com um trecho aberto, um
 * rascunho não salvo ou uma gravação em curso nesta saída, "Conferir e assinar" (no cabeçalho) fica
 * desligado e diz o porquê (`pendenciaDoDocumento`). Na barra da aba Exame, o mesmo estado vira um
 * selo curto — "Salvo", "Editando", "Rascunho não salvo", "Gravando…" —, com o porquê no `title`. O
 * bloqueio de emissão (GUS-208) segue na conferência final, sem mudança.
 */
import { Badge } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';

import type { EdicaoNoDocumento } from './useEdicaoNoDocumento';

const t = ptBR.documentoDaMesa;

/**
 * Por que ainda não dá para conferir e assinar; `null` quando o texto está todo salvo. E202.F3.C01
 * (F-E202.F3-01): a gravação em curso também segura, até a versão estar salva.
 */
export function pendenciaDoDocumento(edicao: EdicaoNoDocumento | null): string | null {
  if (edicao === null) return null;
  if (edicao.salvando) return t.fim.salvando;
  if (edicao.aberto !== null) return t.fim.aberto;
  return edicao.rascunhos.size > 0 ? t.fim.rascunho : null;
}

type EstadoDoTexto = keyof Omit<typeof t.selo, 'rotulo'>;

export function estadoDoTexto(edicao: EdicaoNoDocumento | null): EstadoDoTexto {
  if (edicao === null) return 'soLeitura';
  if (edicao.salvando) return 'gravando';
  if (edicao.aberto !== null) return 'editando';
  return edicao.rascunhos.size > 0 ? 'rascunho' : 'salvo';
}

const VARIANTE: Record<EstadoDoTexto, 'success' | 'warning' | 'info' | 'neutral'> = {
  salvo: 'success',
  editando: 'info',
  rascunho: 'warning',
  gravando: 'info',
  soLeitura: 'neutral',
};

export function SeloDoTexto({ edicao }: { edicao: EdicaoNoDocumento | null }): JSX.Element {
  const estado = estadoDoTexto(edicao);
  const porque = pendenciaDoDocumento(edicao) ?? (estado === 'salvo' ? t.fim.pronto : undefined);
  return (
    <Badge
      variant={VARIANTE[estado]}
      title={porque}
      aria-label={`${t.selo.rotulo}: ${t.selo[estado]}`}
      data-testid="selo-do-texto"
      data-estado={estado}
    >
      {t.selo[estado]}
    </Badge>
  );
}
